import { ActionIcon, Menu } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisH } from '@fortawesome/pro-solid-svg-icons/faEllipsisH';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { inventoryConnectionMenuMessages } from './messages';
import { INVENTORY_SYNC_OPEN_SYNC_CONFIG_MODAL, INVENTORY_SYNC_OPEN_SYNC_DETAILS_MODAL } from '../../../constants/analytics-events';
import { Segment } from '../../../services/analytics-services/segment';
import { SyncConfigModal } from '../sync-config-modal/sync-config-modal';
import { useModal } from '../../../hooks/use-modal';

import type { InventorySyncConnection } from '../../types/inventory-sync-types';
import type { MessageDescriptor } from 'react-intl';
import type { ReactNode } from 'react';

export type DetailsModalRenderProps = {
  connection: InventorySyncConnection;
  onClose: () => void;
};

type InventoryConnectionMenuProps = {
  connection: InventorySyncConnection;
  onSaveConfig: () => void;
  canEditSyncConfig?: boolean;
  readOnly?: boolean;
  renderDetailsModal?: (props: DetailsModalRenderProps) => ReactNode;
  menuLabel?: MessageDescriptor;
  detailsItemLabel?: MessageDescriptor;
};

/**
 * The overflow menu on an Inventory Sync target: Sync Options, which is the same modal everywhere, and
 * a details view that differs per surface. Both the legacy settings page and the refreshed one render
 * this, so the two Segment events fire from exactly one place. With neither item on offer the whole
 * menu goes away, rather than leaving a toggle that opens an empty dropdown.
 */
export const InventoryConnectionMenu = ({
  connection,
  onSaveConfig,
  canEditSyncConfig = true,
  readOnly = false,
  renderDetailsModal,
  menuLabel = inventoryConnectionMenuMessages.menuLabel,
  detailsItemLabel = inventoryConnectionMenuMessages.syncDetails,
}: InventoryConnectionMenuProps) => {
  const intl = useIntl();
  const { open: openSyncConfigModal, close: closeSyncConfigModal, isOpen: isSyncConfigModalOpen } = useModal();
  const { open: openDetailsModal, close: closeDetailsModal, isOpen: isDetailsModalOpen } = useModal();

  const onOpenSyncConfigModal = useCallback(() => {
    Segment.trackEvent(INVENTORY_SYNC_OPEN_SYNC_CONFIG_MODAL, { connection });
    openSyncConfigModal();
  }, [connection, openSyncConfigModal]);

  const onOpenDetailsModal = useCallback(() => {
    Segment.trackEvent(INVENTORY_SYNC_OPEN_SYNC_DETAILS_MODAL, { connection });
    openDetailsModal();
  }, [connection, openDetailsModal]);

  if (!canEditSyncConfig && !readOnly && !renderDetailsModal) {
    return null;
  }

  return (
    <>
      <Menu position='bottom-end'>
        <Menu.Target>
          <ActionIcon aria-label={intl.formatMessage(menuLabel, { connectionName: connection.name })} color='secondary' variant='ghost'>
            <FontAwesomeIcon icon={faEllipsisH} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          {canEditSyncConfig || readOnly ? (
            <Menu.Item onClick={onOpenSyncConfigModal}>{intl.formatMessage(inventoryConnectionMenuMessages.syncOptions)}</Menu.Item>
          ) : null}
          {renderDetailsModal ? <Menu.Item onClick={onOpenDetailsModal}>{intl.formatMessage(detailsItemLabel)}</Menu.Item> : null}
        </Menu.Dropdown>
      </Menu>
      {isSyncConfigModalOpen ? (
        <SyncConfigModal
          connection={connection}
          onClose={closeSyncConfigModal}
          onSaveConfig={onSaveConfig}
          readOnly={readOnly}
          targetName={connection.name}
        />
      ) : null}
      {isDetailsModalOpen && renderDetailsModal ? renderDetailsModal({ connection, onClose: closeDetailsModal }) : null}
    </>
  );
};
